/**
 * patientSchema.ts — reading the API's tailored `$schema` document.
 *
 * A schema is a property of (resource × profession × unit), never of a URL, so
 * the server answers with a JSON Schema document resolved for THIS caller at a
 * unit rather than with a static field list. `$defs.create` is the body a write
 * sends; `$defs.item` is what a read returns; each property carries an
 * `x-tricorder` annotation with the metadata the form needs (classes, value
 * codings, strategy, label). Draft 2020-12 permits unknown annotation keywords,
 * which is why one document serves both validation and rendering.
 *
 * The document is NOT re-declared as a zod mirror. A JSON Schema is validated by
 * a JSON Schema validator; a second copy of its shape in another language is the
 * drift this whole engine exists to remove. What this module does is the one
 * thing a mirror could not: turn the document into the field descriptors the
 * current form controls take.
 */

import Ajv2020 from 'ajv/dist/2020';
import type { AttributeCoding, PatientAttributeField } from './types';

/**
 * The one Ajv instance that knows the Draft 2020-12 dialect.
 *
 * `strict: false` because the document deliberately carries an unknown
 * annotation keyword (`x-tricorder`), which the dialect permits and strict mode
 * would reject; `validateFormats: false` because nothing here declares a
 * format, and loading the format library to check none would be cost for
 * nothing.
 */
const metaValidator = new Ajv2020({ strict: false, validateFormats: false });

/**
 * Is this response a valid JSON Schema?
 *
 * Checked against the JSON Schema META-SCHEMA — the dialect's own definition of
 * what a schema is — and NOT against a zod copy of the document's shape. A
 * mirror would be a second statement of the thing the whole engine exists to
 * keep in one place, and it would go stale the first time the server added a
 * keyword. `validateSchema` answers the only question worth asking on this
 * boundary: can the renderer and the validator both read it?
 */
export function isSchemaDocument(doc: unknown): doc is PatientSchemaDocument {
  if (typeof doc !== 'object' || doc === null) return false;
  return metaValidator.validateSchema(doc as object) === true;
}

/** The `validateSchema` errors of the last failed {@link isSchemaDocument}, for
 *  a log line that names what was wrong rather than "invalid". */
export function schemaDocumentErrors(): string {
  return metaValidator.errorsText(metaValidator.errors);
}

/** One property of a derived shape, plus the platform's own annotation. */
interface SchemaProperty {
  type?: string | string[];
  enum?: unknown[];
  pattern?: string;
  minimum?: number;
  maximum?: number;
  'x-tricorder'?: {
    key?: string;
    required?: boolean;
    label?: string;
    classes?: string[] | null;
    codings?: Record<string, AttributeCoding> | null;
    strategy?: string;
    link_fields?: string[] | null;
    dedup?: string;
  };
}

interface SchemaShape {
  properties?: Record<string, SchemaProperty>;
  required?: string[];
}

/** The document `GET /patients/$schema` and `GET /patients/{id}/$schema` serve. */
export interface PatientSchemaDocument {
  $id?: string;
  $defs?: {
    create?: SchemaShape;
    item?: SchemaShape;
  };
}

/**
 * The WRITABLE fields of a schema document, in stable key order.
 *
 * Read off `$defs.create`, so a server-minted field is absent by construction —
 * the form cannot render a control for a value the client may not send, and no
 * strategy check in the UI is what decides that.
 */
export function writableFields(doc: PatientSchemaDocument | undefined): PatientAttributeField[] {
  return fieldsOf(doc?.$defs?.create);
}

/** The READABLE fields of a schema document, in stable key order. */
export function readableFields(doc: PatientSchemaDocument | undefined): PatientAttributeField[] {
  return fieldsOf(doc?.$defs?.item);
}

function fieldsOf(shape: SchemaShape | undefined): PatientAttributeField[] {
  const properties = shape?.properties ?? {};
  const required = new Set(shape?.required ?? []);
  return Object.keys(properties)
    .sort()
    .map((key) => {
      const property = properties[key];
      const meta = property['x-tricorder'] ?? {};
      return {
        key,
        // The JSON Schema type drives the control AND the coercion on submit:
        // a ward may declare an integer, and a form that posted "12" would be
        // refused by the same constraint that made the field an integer.
        type: jsonType(property),
        required: required.has(key),
        values: enumValues(property),
        pattern: property.pattern ?? '',
        strategy: meta.strategy ?? '',
        link_fields: meta.link_fields ?? null,
        dedup: meta.dedup ?? '',
        label: meta.label ?? '',
        classes: meta.classes ?? null,
        value_codings: meta.codings ?? null,
      };
    });
}

/** The single JSON Schema type name of a property, or "string" when it declares none. */
function jsonType(property: SchemaProperty): string {
  if (Array.isArray(property.type)) {
    return property.type.find((t) => t !== 'null') ?? 'string';
  }
  if (property.type) return property.type;
  // A closed disjunction of strings generates `enum` and no `type`.
  const values = property.enum ?? [];
  return typeof values[0] === 'number' ? 'number' : 'string';
}

/** A field's allowed values as strings, or null when it is not a closed set. */
function enumValues(property: SchemaProperty): string[] | null {
  if (!property.enum || property.enum.length === 0) return null;
  return property.enum.map((v) => String(v));
}

/**
 * Coerce one form value to the type its field declares.
 *
 * Every control in this form edits text, and every schema constraint is typed.
 * The conversion belongs here, at the one boundary between the two, rather than
 * in each control: an integer field given "12" must be sent as 12, and given
 * something unparseable must be sent AS TYPED so the server's own violation
 * names the field rather than the form silently dropping it.
 */
export function coerceToField(field: PatientAttributeField, value: string): unknown {
  if (field.type !== 'integer' && field.type !== 'number') return value;
  const n = Number(value);
  return value.trim() !== '' && Number.isFinite(n) ? n : value;
}

/** Render a stored value for a text control. */
export function displayValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}
